// MLS comp reading and CMA adjustment math for the Resi CMA Builder.
// Reads "Residential Customer Financial Report" records from SWFLMLS PDFs and
// adjusts each comp's price by the feature differences from the subject.

// Features the MLS report can confirm. Plantation shutters do not appear in
// these reports, so they are never used for an adjustment.
export const ADJUSTABLE_FEATURES = ['den', 'pool', 'spa', 'outdoorKitchen', 'hurricaneImpact', 'generator'];

const squash = (s) => (s || '').replace(/\s+/g, '').toLowerCase();

const field = (text, re) => {
  const m = text.match(re);
  return m ? m[1].trim() : '';
};

const toNumber = (s) => {
  const n = parseFloat(String(s || '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// Splits a full PDF text dump into one block per property record.
export function splitRecords(fullText) {
  const parts = String(fullText || '').split(/Residential\s*Customer\s*Financial\s*Report/i);
  return parts.slice(1).map((p) => `Residential Customer Financial Report\n${p}`);
}

// Reads the fields Resi needs from one record block.
export function parseRecord(block) {
  const text = block || '';
  const status = field(text, /Status:\s*([A-Za-z]+)/);
  const statusDate = field(text, /Status:\s*\w+\s*\(([\d/]+)\)/);
  const mls = field(text, /MLS#:\s*(\d+)/);
  const address = field(text, /Address:\s*([^\n]+)/);
  const listPrice = toNumber(field(text, /List\s*Price:\s*\$([\d,]+)/));
  const soldPrice = toNumber(field(text, /Sold\s*Price:\s*\$([\d,]+)/));
  const sqft = toNumber(field(text, /Approx\.?\s*Living Area:\s*([\d,]+)/));
  const beds = field(text, /Bedrooms:\s*([^\n]+)/);
  const baths = field(text, /Baths:\s*(\d+)/);

  const pool = /Private Pool:\s*Yes/i.test(text);
  const spa = /Private Spa:\s*Yes/i.test(text);
  const exteriorSeg = field(text, /Exterior Features:([\s\S]*?)Interior Features:/i);
  const equipmentSeg = field(text, /Equipment:([\s\S]*?)Exterior Features:/i);
  const storm = field(text, /Storm Protection:([^\n]*)/i);

  const features = {
    den: /den-study|den-/i.test(squash(text)),
    pool,
    spa,
    outdoorKitchen: /outdoorkitchen|builtingrill/.test(squash(exteriorSeg)),
    hurricaneImpact: /impact|shutters/i.test(storm),
    generator: /generator/.test(squash(equipmentSeg)),
  };

  const isSold = /sold/i.test(status);
  const price = isSold && soldPrice ? soldPrice : listPrice;

  return { mls, status, statusDate, address, listPrice, soldPrice, price, sqft, beds, baths, features };
}

// Dollar adjustment for one comp. A feature the subject has and the comp lacks
// adds its value to the comp; a feature the comp has and the subject lacks subtracts it.
export function adjustComp(comp, subjectFeatures, values) {
  let total = 0;
  const lines = [];
  ADJUSTABLE_FEATURES.forEach((key) => {
    const value = toNumber(values?.[key]) || 0;
    if (!value) return;
    const diff = (subjectFeatures?.[key] ? 1 : 0) - (comp.features?.[key] ? 1 : 0);
    if (!diff) return;
    const amount = diff * value;
    total += amount;
    lines.push({ key, amount });
  });
  return { ...comp, adjustment: total, adjusted: (comp.price || 0) + total, lines };
}

// Splits adjusted comps into sold, pending and active groups and computes the
// average adjusted sold price. Pending and active comps are not averaged.
export function runCompAnalysis({ comps, subjectFeatures, values }) {
  const adjusted = (comps || [])
    .filter((c) => c.price)
    .map((c) => adjustComp(c, subjectFeatures, values));
  const group = (re) => adjusted.filter((c) => re.test(c.status || ''));
  const sold = group(/sold/i);
  const pending = group(/pending/i);
  const active = group(/active/i);
  const avgAdjustedSold = sold.length
    ? sold.reduce((sum, c) => sum + c.adjusted, 0) / sold.length
    : 0;
  return { sold, pending, active, avgAdjustedSold };
}

// Feature comparison for one comp against the subject.
export function featureDifferences(comp, subjectFeatures) {
  const labels = {
    den: 'Den', pool: 'Pool', spa: 'Spa', outdoorKitchen: 'Outdoor kitchen',
    hurricaneImpact: 'Hurricane protection', generator: 'Whole home generator',
  };
  const compHas = [];
  const subjectHas = [];
  Object.keys(labels).forEach((key) => {
    const c = !!comp.features?.[key];
    const s = !!subjectFeatures?.[key];
    if (c && !s) compHas.push(labels[key]);
    if (s && !c) subjectHas.push(labels[key]);
  });
  return { compHas, subjectHas };
}
